// useSubscription — single source of truth for billing state in the UI.
//
// Loads profile + active subscription + unconsumed design credits for the
// currently signed-in user. Subscribes to realtime changes so the UI
// updates instantly when Stripe webhook posts a new subscription.
//
// Returns:
//   loading              - initial load flag
//   tier                 - 'free' | 'customer_pro' | 'artist_pro'
//   status               - Stripe status string or 'trialing' (app-side) or 'expired'
//   trialDaysLeft        - customer trial days remaining
//   artistTrialStarted   - boolean; true once they've clicked "Become a Tattune artist"
//   artistTrialDaysLeft  - artist trial days remaining (negative = expired, 0 = never started)
//   credits              - count of unconsumed design_credits rows
//   isEntitled(kind)     - boolean; kind = 'save' | 'export_hd' | 'artist_profile' | 'flash_upload' | 'leads'
//   startArtistTrial()   - kick off the artist trial (idempotent server-side)
//   refresh()            - force a re-fetch (useful after returning from Checkout)
//
// Entitlement rules live in this file on purpose — keep them in one place.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../../auth/AuthProvider";
import { TRIAL_DAYS, ARTIST_TRIAL_DAYS } from "../billing/constants";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function daysBetween(a, b) {
  return Math.floor((b.getTime() - a.getTime()) / MS_PER_DAY);
}

export function useSubscription() {
  const { session, loading: authLoading } = useAuth();
  const userId = session?.user?.id ?? null;

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null); // most recent row
  const [credits, setCredits] = useState(0);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    if (!userId) {
      setProfile(null);
      setSubscription(null);
      setCredits(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    const [profileRes, subRes, creditsRes] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, role, trial_started_at, artist_trial_started_at, stripe_customer_id")
        .eq("id", userId)
        .maybeSingle(),
      supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("design_credits")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("consumed", false),
    ]);

    if (!mountedRef.current) return;

    if (profileRes.error) {
      console.warn("[useSubscription] profile load error", profileRes.error);
    }
    if (subRes.error) {
      console.warn("[useSubscription] subscription load error", subRes.error);
    }
    if (creditsRes.error) {
      console.warn("[useSubscription] credits load error", creditsRes.error);
    }

    setProfile(profileRes.data ?? null);
    setSubscription(subRes.data ?? null);
    setCredits(creditsRes.count ?? 0);
    setLoading(false);
  }, [userId]);

  // Initial + whenever user changes.
  useEffect(() => {
    mountedRef.current = true;
    if (authLoading) return;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [authLoading, load]);

  // Realtime: push updates when Stripe webhook writes.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`billing-self-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
          filter: `user_id=eq.${userId}`,
        },
        () => load()
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "design_credits",
          filter: `user_id=eq.${userId}`,
        },
        () => load()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, load]);

  // ---------- derive display state ----------
  const derived = useMemo(() => {
    const now = new Date();

    // Customer trial math (app-side; no Stripe trial involved).
    // Starts at signup (profiles.trial_started_at has default now()).
    const trialStart = profile?.trial_started_at
      ? new Date(profile.trial_started_at)
      : null;
    const customerDaysUsed = trialStart ? daysBetween(trialStart, now) : 0;
    const trialDaysLeft = trialStart ? TRIAL_DAYS - customerDaysUsed : 0;

    // Artist trial math. Null means they have never tried to become an artist
    // — distinguish that from "trial expired."
    const artistTrialStart = profile?.artist_trial_started_at
      ? new Date(profile.artist_trial_started_at)
      : null;
    const artistTrialStarted = !!artistTrialStart;
    const artistDaysUsed = artistTrialStart
      ? daysBetween(artistTrialStart, now)
      : 0;
    const artistTrialDaysLeft = artistTrialStart
      ? ARTIST_TRIAL_DAYS - artistDaysUsed
      : 0;
    const artistTrialActive = artistTrialStarted && artistTrialDaysLeft > 0;

    // Active subscription takes precedence over everything.
    const subActive =
      subscription &&
      ["active", "trialing"].includes(subscription.status) &&
      new Date(subscription.current_period_end).getTime() > now.getTime();

    let tier = "free";
    let status = "free";

    if (subActive) {
      tier = subscription.plan; // 'customer_pro' | 'artist_pro'
      status = subscription.status;
    } else if (trialDaysLeft > 0 && !subscription) {
      tier = "free";
      status = "trialing";
    } else if (profile && trialDaysLeft <= 0 && !subActive) {
      tier = "free";
      status = "expired";
    }

    return {
      tier,
      status,
      trialDaysLeft,
      artistTrialStarted,
      artistTrialDaysLeft,
      artistTrialActive,
    };
  }, [profile, subscription]);

  const isEntitled = useCallback(
    (kind) => {
      const { tier, status, artistTrialActive } = derived;

      const customerTier = tier === "customer_pro" || tier === "artist_pro";
      const artistTier = tier === "artist_pro";
      const inCustomerTrial = status === "trialing";
      const hasCredit = credits > 0;

      switch (kind) {
        case "save":
          // Customers: trial, any paid plan, or an unconsumed credit.
          return customerTier || inCustomerTrial || hasCredit;
        case "export_hd":
          return customerTier;
        case "artist_profile":
        case "flash_upload":
        case "leads":
          // Artist tier OR still within the 30-day artist trial window.
          return artistTier || artistTrialActive;
        default:
          console.warn(`[useSubscription] unknown entitlement kind: ${kind}`);
          return false;
      }
    },
    [derived, credits]
  );

  // Kick off the artist trial. Calls a security-definer RPC so the client
  // doesn't need UPDATE privileges on profiles. Idempotent: if the trial
  // was already started, the RPC returns the existing timestamp unchanged.
  const startArtistTrial = useCallback(async () => {
    const { data, error } = await supabase.rpc("start_artist_trial");
    if (error) {
      console.error("[useSubscription] startArtistTrial RPC error", error);
      throw error;
    }
    // Optimistically update local profile so the hook returns the new value
    // immediately without waiting for a refetch.
    setProfile((prev) =>
      prev ? { ...prev, artist_trial_started_at: data } : prev
    );
    return data;
  }, []);

  return {
    loading: loading || authLoading,
    userId,
    tier: derived.tier,
    status: derived.status,
    trialDaysLeft: derived.trialDaysLeft,
    artistTrialStarted: derived.artistTrialStarted,
    artistTrialDaysLeft: derived.artistTrialDaysLeft,
    artistTrialActive: derived.artistTrialActive,
    credits,
    subscription,
    profile,
    isEntitled,
    startArtistTrial,
    refresh: load,
  };
}
